import React, { useMemo } from 'react';
import clsx from 'clsx';
import { resolveFormLayout } from '@repo/lib';
import useCheckoutState from '../../../../hooks/useCheckoutState';
import StyledElement from '../../../StyledElement';
import isFieldEnabled from '../FormField/isFieldEnabled';
import isFieldHidden from '../FormField/isFieldHidden';
import DiscountCodeBox from '../DiscountCodeBox';

export function FormHeading({ item }) {
  const prefix = item.level === 'subheading' ? 'subheading' : 'heading';
  return (
    <StyledElement
      elementPath={`checkout.form.${prefix}`}
      as={item.level === 'subheading' ? 'h3' : 'h2'}
      data-testid="form-heading"
      className={clsx(`cp_form-${prefix}`, 'col-span-full')}
    >
      {item.text}
    </StyledElement>
  );
}

export function FormDivider() {
  return (
    <StyledElement
      elementPath="checkout.form.divider"
      as="hr"
      data-testid="form-divider"
      className="cp_form-divider col-span-full"
    />
  );
}

function FormGroup({ group, renderField, phones, isShown }) {
  const { checkout } = useCheckoutState();
  const fieldItems = group.items.filter(child => child.type === 'field');

  // A group whose fields are all hidden takes its headings with it, so no title floats over nothing.
  const showsDiscountCode =
    checkout.showCouponCodeField && group.items.some(child => child.type === 'discount-code');
  if (!showsDiscountCode && !fieldItems.some(({ field }) => isShown(field))) return null;

  return (
    <div
      className="cp_form-group mb-4 grid gap-x-[var(--cp_checkout-form-groups-gap)] gap-y-[var(--cp_checkout-form-groups-rowGap)]"
      data-columns={group.columns}
      data-phones={phones}
      style={{ '--cp_form-group-columns': group.columns }}
    >
      {group.items.map(child => {
        if (child.disabled) return null;
        if (child.type === 'heading') return <FormHeading key={child.id} item={child} />;
        if (child.type === 'divider') return <FormDivider key={child.id} />;
        if (child.type === 'discount-code') {
          return checkout.showCouponCodeField ? (
            <div key={child.id} className="cp_form-group-cell min-w-0">
              <DiscountCodeBox />
            </div>
          ) : null;
        }
        // A hidden field takes no cell, so the fields after it close up.
        if (!isShown(child.field)) return null;
        return (
          <div key={child.fieldId} className="cp_form-group-cell min-w-0">
            {renderField(child.field)}
          </div>
        );
      })}
    </div>
  );
}

/** Resolved layout items: headings, dividers and groups around whatever `renderField` draws. */
export function FormLayoutItems({ items, renderField, isShown = () => true }) {
  const { theme } = useCheckoutState();
  const phones = theme?.elements?.checkout?.form?.groups?.phones === 'keep' ? 'keep' : 'stack';

  return items.map(item => {
    if (item.disabled) return null;
    if (item.type === 'heading') return <FormHeading key={item.id} item={item} />;
    if (item.type === 'divider') return <FormDivider key={item.id} />;
    if (item.type === 'discount-code') return <DiscountCodeBox key={item.id} className="mb-4" />;
    if (item.type === 'group') {
      return (
        <FormGroup
          key={item.id}
          group={item}
          renderField={renderField}
          phones={phones}
          isShown={isShown}
        />
      );
    }
    return <React.Fragment key={item.fieldId}>{renderField(item.field)}</React.Fragment>;
  });
}

export default function FormLayoutFields({ renderField }) {
  const { checkout, form, isPreview, page, query } = useCheckoutState();

  const items = useMemo(
    () => resolveFormLayout(checkout.formLayout, Object.values(form.fields ?? {})),
    [checkout.formLayout, form.fields],
  );

  const isShown = field =>
    isPreview ||
    (!isFieldHidden(field, isPreview, query) && isFieldEnabled(field, form.fields, page.fields));

  return <FormLayoutItems items={items} renderField={renderField} isShown={isShown} />;
}
